/**
 * Resolve once `isStable()` returns true, polling on animation frames, with a
 * hard `timeoutMs` ceiling so a never-settling signal can't hang the caller.
 *
 * Resolves with WHETHER the signal actually stabilized: `true` if `isStable()`
 * became true within the ceiling, `false` if the ceiling was hit while still
 * unstable. Callers gating an export on a deferred preview MUST check this and
 * fail loudly on `false` rather than capture — otherwise a sustained deferred
 * backlog (the user is still typing while hammering Download on a throttled
 * handset) silently rasterizes a stale frame whose filename/metadata reflect
 * the live state. See the export call sites for the abort-with-toast pattern.
 *
 * Used before an html2canvas capture on generators whose preview renders from a
 * `useDeferredValue` snapshot: if the user edits copy or switches platform and
 * immediately hits Download, the deferred preview tree may not have committed
 * the latest state yet, so a synchronous capture could rasterize the previous
 * frame. Gating capture on "deferred === live" guarantees the exported PNG
 * matches what the controls (and the filename) report. The animation-frame poll
 * means we wait at most a frame or two in practice (the deferred render commits
 * as soon as the main thread is free), and never longer than the ceiling.
 */
export function waitForStable(isStable: () => boolean, timeoutMs = 600): Promise<boolean> {
  if (isStable()) return Promise.resolve(true)
  // No rAF (non-browser / test env): can't poll, so report the current signal
  // rather than hang or falsely claim stability.
  if (typeof requestAnimationFrame !== 'function') return Promise.resolve(isStable())

  const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now())
  const start = now()
  return new Promise<boolean>((resolve) => {
    const tick = (): void => {
      const stable = isStable()
      if (stable || now() - start >= timeoutMs) {
        resolve(stable)
        return
      }
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  })
}
